// Small presentational pieces shared by the Telemetrie sections. Extracted
// from the former AdminProductAnalyticsTab so the look stays identical.
import type { ReactNode } from "react";

const CARD = "rounded-2xl bg-white border border-[rgba(10,52,96,0.10)] shadow-[0_4px_8px_rgba(10,52,96,0.06)]";

export function Card({ caption, children, className = "" }: { caption?: string; children: ReactNode; className?: string }) {
  return (
    <div className={`${CARD} p-5 ${className}`}>
      {caption ? <p className="cs-caption mb-3">{caption}</p> : null}
      {children}
    </div>
  );
}

export function StatCard({ label, value, loading, hint }: { label: string; value: number; loading: boolean; hint?: string }) {
  return (
    <div className={`${CARD} p-5`}>
      <p className="cs-caption mb-2">{label}</p>
      <p className="text-3xl font-semibold text-[var(--cs-navy)] tabular-nums">
        {loading ? "…" : value.toLocaleString("nl-BE")}
      </p>
      {hint ? <p className="mt-1 text-xs text-[var(--cs-ink-muted)]">{hint}</p> : null}
    </div>
  );
}

const PERIODS = [
  { label: "7d", days: 7 },
  { label: "30d", days: 30 },
  { label: "90d", days: 90 },
];

export function PeriodToggle({ days, onChange }: { days: number; onChange: (d: number) => void }) {
  return (
    <div className="inline-flex rounded-full border border-[rgba(10,52,96,0.10)] bg-white p-1">
      {PERIODS.map((p) => {
        const active = p.days === days;
        return (
          <button
            key={p.label}
            type="button"
            onClick={() => { onChange(p.days); }}
            className={`px-3 py-1 text-xs rounded-full transition-colors ${
              active ? "bg-[rgba(225,29,72,0.06)] text-[var(--cs-navy)]" : "text-[var(--cs-ink-muted)] hover:text-[var(--cs-navy)]"
            }`}
          >
            {p.label}
          </button>
        );
      })}
    </div>
  );
}

export function Bar({ value, max, tone = "navy" }: { value: number; max: number; tone?: "navy" | "rose" | "amber" }) {
  const width = max > 0 ? Math.max(2, Math.round((value / max) * 100)) : 0;
  const fill = tone === "rose" ? "bg-[var(--cs-rose)]" : tone === "amber" ? "bg-amber-500" : "bg-[var(--cs-navy)]";
  return (
    <div className="h-1.5 w-full rounded-full bg-[rgba(10,52,96,0.08)]">
      <div className={`h-1.5 rounded-full ${fill}`} style={{ width: `${width}%` }} />
    </div>
  );
}

export function ErrorBanner({ message }: { message: string }) {
  return (
    <div className="rounded-2xl border border-[rgba(225,29,72,0.20)] bg-[rgba(225,29,72,0.06)] p-4 text-sm text-[var(--cs-rose)]">
      {message}
    </div>
  );
}
